// AI Ghostwriter Pro - Landing Page
// For aighostwriterpro.com domain
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SeoHead } from '../components/SeoHead';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { 
  BookOpen, Sparkles, CheckCircle, Star, ArrowRight, 
  Clock, Shield, Zap, Users, Award, ChevronRight,
  FileText, Target, PenTool, Rocket, MessageCircle, Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Static SEO schemas - embedded for immediate rendering (crawlers don't wait for JS)
const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "AI Ghostwriter Pro",
  "alternateName": "AIGhostwriterPro",
  "url": "https://aighostwriterpro.com",
  "description": "Professional AI-powered book ghostwriting service. Get your book written by AI in days, not months.",
  "parentOrganization": { "@type": "Organization", "name": "Web-Tech Services", "url": "https://upost.com" },
  "address": { "@type": "PostalAddress", "addressLocality": "Arlington", "addressRegion": "TX", "postalCode": "76010", "addressCountry": "US" },
  "sameAs": ["https://upost.com", "https://upost.com/ghostwriter"]
};

const SERVICE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "AI Ghostwriting Service",
  "name": "AI Ghostwriter Pro - Book Writing Service",
  "description": "Professional AI-powered ghostwriting service that creates complete, publication-ready books. From idea to manuscript in 7-14 days.",
  "provider": { "@type": "Organization", "name": "AI Ghostwriter Pro", "url": "https://aighostwriterpro.com" },
  "areaServed": { "@type": "Country", "name": "Worldwide" },
  "hasOfferCatalog": {
    "@type": "OfferCatalog", "name": "Ghostwriting Packages",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Starter Package", "description": "Up to 20,000 words, 10 chapters, 3-5 day delivery" }, "price": "497", "priceCurrency": "USD" },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Professional Package", "description": "Up to 50,000 words, 25 chapters, 7-10 day delivery" }, "price": "997", "priceCurrency": "USD" },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Complete Package", "description": "Up to 70,000 words, unlimited revisions, 10-14 day delivery" }, "price": "1497", "priceCurrency": "USD" }
    ]
  }
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    { "@type": "Question", "name": "How does AI ghostwriting work?", "acceptedAnswer": { "@type": "Answer", "text": "You provide your book concept, genre, and preferences. Our AI creates a detailed outline for your approval, then writes each chapter. You review and request revisions until satisfied. The entire process takes 7-14 days." } },
    { "@type": "Question", "name": "Will my AI-written book be unique?", "acceptedAnswer": { "@type": "Answer", "text": "Each book is generated fresh based on your specific inputs. No templates, no recycled content. Your book is 100% original and passes all plagiarism checks." } },
    { "@type": "Question", "name": "How much does AI ghostwriting cost?", "acceptedAnswer": { "@type": "Answer", "text": "Our packages range from $497 for a 20,000-word ebook to $1,497 for a complete 70,000-word book with full publishing support." } },
    { "@type": "Question", "name": "Is AI ghostwriting legal?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, AI ghostwriting is completely legal. You own full rights to the content generated for you. You can publish it under your name on Amazon or any retailer." } },
    { "@type": "Question", "name": "What genres can AI write?", "acceptedAnswer": { "@type": "Answer", "text": "Our AI excels at Romance, Mystery, Thriller, Science Fiction, Fantasy, Self-Help, Business, Biography, and How-To books." } }
  ]
};

const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "AI Ghostwriter Pro",
  "description": "Professional AI-powered book ghostwriting service based in Arlington, Texas. Serving authors worldwide.",
  "address": { "@type": "PostalAddress", "addressLocality": "Arlington", "addressRegion": "TX", "postalCode": "76010", "addressCountry": "US" },
  "geo": { "@type": "GeoCoordinates", "latitude": "32.7357", "longitude": "-97.1081" },
  "url": "https://aighostwriterpro.com",
  "priceRange": "$497 - $1497",
  "parentOrganization": { "@type": "Organization", "name": "Web-Tech Services" }
};

const ALL_SCHEMAS = [ORGANIZATION_SCHEMA, SERVICE_SCHEMA, FAQ_SCHEMA, LOCAL_BUSINESS_SCHEMA];

export function AIGhostwriterLanding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '', genre_interest: '' });
  const [contactStatus, setContactStatus] = useState(null);

  const isGhostwriterDomain = window.location.hostname === 'aighostwriterpro.com' || window.location.hostname === 'www.aighostwriterpro.com';
  const canonicalBase = isGhostwriterDomain ? 'https://aighostwriterpro.com' : 'https://upost.com/ghostwriter';

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ghostwriter/packages`);
      const data = await response.json();
      setPackages(data.packages || []);
    } catch (err) {
      console.error('Failed to fetch packages:', err);
    }
  };

  const handleGetStarted = (packageId) => {
    if (user) {
      navigate(`/ghostwriter/dashboard?package=${packageId}`);
    } else {
      navigate(`/register?redirect=/ghostwriter/dashboard&package=${packageId}`);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactStatus('sending');
    try {
      const resp = await fetch(`${API_URL}/api/ghostwriter/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      if (resp.ok) {
        setContactStatus('sent');
        setContactForm({ name: '', email: '', message: '', genre_interest: '' });
      } else {
        setContactStatus('error');
      }
    } catch {
      setContactStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* SEO */}
      <SeoHead
        title="AI Ghostwriter Pro | Professional AI Book Writing Service | $497-$1497"
        description="Get your book written by AI in 7-14 days. Professional AI ghostwriting service starting at $497. Cheaper than human ghostwriters, faster delivery. Romance, Mystery, Business books & more."
        keywords="AI ghostwriter, AI book writing service, automated book writing, AI ghostwriting service, book ghostwriter online, hire AI to write my book, AI ghostwriter for fiction, best AI book writing service 2026"
        canonical={canonicalBase}
        og={{
          type: 'website',
          url: canonicalBase,
          title: 'AI Ghostwriter Pro - Your Book Written in Days, Not Months',
          description: 'Professional AI book writing service. Complete manuscripts in 7-14 days starting at $497. Save thousands vs human ghostwriters.',
          image: `${canonicalBase}/og-image.jpg`,
          siteName: 'AI Ghostwriter Pro'
        }}
        twitter={{
          card: 'summary_large_image',
          title: 'AI Ghostwriter Pro - Books Written by AI',
          description: 'Get your book written by AI in 7-14 days. Starting at $497.',
          image: `${canonicalBase}/og-image.jpg`
        }}
        geo={{
          region: 'US-TX',
          placename: 'Arlington, Texas',
          position: '32.7357;-97.1081',
          icbm: '32.7357, -97.1081'
        }}
        schemas={ALL_SCHEMAS}
      />
      {/* Navigation */}
      <nav className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PenTool className="w-8 h-8 text-purple-400" />
            <span className="text-2xl font-bold text-white">AI Ghostwriter Pro</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-300 hover:text-white transition">
              UPost Home
            </Link>
            {user ? (
              <Button onClick={() => navigate('/ghostwriter/dashboard')} className="bg-purple-600 hover:bg-purple-700">
                My Projects
              </Button>
            ) : (
              <>
                <Link to="/login" className="text-gray-300 hover:text-white transition">
                  Log In
                </Link>
                <Button onClick={() => navigate('/register')} className="bg-purple-600 hover:bg-purple-700">
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 text-center">
        <div className="inline-flex items-center gap-2 bg-purple-500/20 border border-purple-500/30 px-4 py-2 rounded-full text-purple-300 text-sm mb-8">
          <Sparkles className="w-4 h-4" />
          AI-Powered Book Writing Service
        </div>
        
        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
          Your Book, Written by AI.<br />
          <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Published by You.
          </span>
        </h1>
        
        <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
          Stop staring at a blank page. Our AI ghostwriters create complete, 
          publication-ready books in days, not months. From idea to bestseller.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <Button 
            size="lg" 
            onClick={() => handleGetStarted('professional')}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg px-8 py-6"
            data-testid="hero-cta-btn"
          >
            Start Your Book Now <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <Button 
            size="lg" 
            variant="outline"
            onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
            className="border-purple-400 text-purple-300 hover:bg-purple-500/20 text-lg px-8 py-6"
          >
            View Pricing
          </Button>
        </div>

        {/* Social Proof */}
        <div className="flex flex-wrap justify-center gap-8 text-gray-400">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            <span>100+ Books Written</span>
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-400" />
            <span>4.9/5 Average Rating</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            <span>7-14 Day Delivery</span>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
          How It Works
        </h2>
        <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
          From concept to complete manuscript in 4 simple steps
        </p>

        <div className="grid md:grid-cols-4 gap-8">
          {[
            { icon: Target, title: "Share Your Vision", desc: "Tell us your book idea, genre, and preferences" },
            { icon: FileText, title: "Review Outline", desc: "We create a detailed chapter-by-chapter outline for your approval" },
            { icon: PenTool, title: "AI Writes", desc: "Our AI ghostwriter crafts your complete manuscript" },
            { icon: Rocket, title: "Publish", desc: "Receive your publication-ready book in all formats" }
          ].map((step, i) => (
            <div key={i} className="relative">
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 text-center hover:border-purple-500/50 transition-all">
                <div className="w-16 h-16 bg-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-8 h-8 text-purple-400" />
                </div>
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                  {i + 1}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                <p className="text-gray-400">{step.desc}</p>
              </div>
              {i < 3 && (
                <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                  <ChevronRight className="w-8 h-8 text-purple-500/50" />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Comparison Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="bg-gradient-to-r from-slate-800/50 to-purple-900/30 border border-slate-700 rounded-3xl p-8 md:p-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-8">
            Why AI Ghostwriter Pro?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-red-400 line-through mb-2">$5,000 - $50,000</div>
              <div className="text-gray-400">Human Ghostwriters</div>
              <div className="text-sm text-gray-500 mt-2">3-6 months delivery</div>
            </div>
            <div className="text-center border-x border-slate-700 px-8">
              <div className="text-4xl font-bold text-yellow-400 line-through mb-2">$1,995+</div>
              <div className="text-gray-400">Writing Courses</div>
              <div className="text-sm text-gray-500 mt-2">DIY - months of work</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-400 mb-2">$497 - $1,497</div>
              <div className="text-white font-semibold">AI Ghostwriter Pro</div>
              <div className="text-sm text-green-400 mt-2">7-14 day delivery</div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full">
              <CheckCircle className="w-5 h-5" />
              Save thousands and get your book faster
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="container mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
          Choose Your Package
        </h2>
        <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
          All packages include professional AI writing, multiple formats, and satisfaction guarantee
        </p>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {packages.map((pkg) => (
            <Card 
              key={pkg.id} 
              className={`relative bg-slate-800/50 border-slate-700 ${pkg.popular ? 'border-purple-500 scale-105' : ''}`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>
              )}
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl text-white">{pkg.name}</CardTitle>
                <CardDescription className="text-gray-400">{pkg.description}</CardDescription>
                <div className="mt-4">
                  <span className="text-5xl font-bold text-white">${pkg.price}</span>
                  <span className="text-gray-400"> / book</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-300">
                      <CheckCircle className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className={`w-full ${pkg.popular ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700' : 'bg-slate-700 hover:bg-slate-600'}`}
                  onClick={() => handleGetStarted(pkg.id)}
                  data-testid={`package-${pkg.id}-btn`}
                >
                  Get Started
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
          Everything You Need to Publish
        </h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: BookOpen, title: "Complete Manuscript", desc: "Full book written to your specifications" },
            { icon: FileText, title: "All Formats", desc: "EPUB, PDF, DOCX, and KDP-ready files" },
            { icon: Target, title: "Niche Research", desc: "Complete package includes market analysis" },
            { icon: PenTool, title: "Professional Editing", desc: "AI editing and polish passes included" },
            { icon: Sparkles, title: "Amazon Optimization", desc: "Keywords, description, and metadata" },
            { icon: Shield, title: "Satisfaction Guarantee", desc: "Revisions until youre happy" }
          ].map((feature, i) => (
            <div key={i} className="bg-slate-800/30 border border-slate-700 rounded-xl p-6 hover:border-purple-500/50 transition-all">
              <feature.icon className="w-10 h-10 text-purple-400 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-gray-400">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
          What Authors Say
        </h2>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            { 
              quote: "I had the idea for my book for years but never had time to write it. AI Ghostwriter Pro delivered a complete manuscript in 10 days!",
              name: "Sarah M.",
              title: "Romance Author"
            },
            { 
              quote: "The quality blew me away. After minor edits, it went straight to Amazon. Now it's selling!",
              name: "James T.",
              title: "Business Author"
            },
            { 
              quote: "The Complete package was worth every penny. Research, writing, and publishing support all in one.",
              name: "Maria L.",
              title: "Self-Help Author"
            }
          ].map((testimonial, i) => (
            <div key={i} className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
              <div className="flex gap-1 mb-4">
                {[1,2,3,4,5].map(n => <Star key={n} className="w-5 h-5 text-yellow-400 fill-yellow-400" />)}
              </div>
              <p className="text-gray-300 mb-4 italic">"{testimonial.quote}"</p>
              <div>
                <div className="font-semibold text-white">{testimonial.name}</div>
                <div className="text-sm text-gray-500">{testimonial.title}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
          Frequently Asked Questions
        </h2>

        <div className="max-w-3xl mx-auto space-y-4">
          {[
            { q: "How does AI ghostwriting work?", a: "You provide your book concept, genre, and preferences. Our AI creates a detailed outline for your approval, then writes each chapter. You review and request revisions until satisfied." },
            { q: "Will my book be unique?", a: "Absolutely. Each book is generated fresh based on your specific inputs. No templates, no recycled content. Your book is 100% original." },
            { q: "Can I edit the content?", a: "Yes! You receive full, editable manuscripts. Make any changes you want before publishing." },
            { q: "What genres can you write?", a: "We support all major genres: Fiction (romance, mystery, thriller, sci-fi, fantasy), Non-fiction (self-help, business, how-to, memoirs), and more." },
            { q: "How long until I get my book?", a: "Starter: 3-5 days, Professional: 7-10 days, Complete: 10-14 days. Complex projects may take slightly longer." },
            { q: "What if I'm not satisfied?", a: "All packages include revision rounds. We work with you until you're happy with the result." }
          ].map((faq, i) => (
            <div key={i} className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">{faq.q}</h3>
              <p className="text-gray-400">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="container mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
          Have Questions? Get in Touch
        </h2>
        <p className="text-gray-400 text-center mb-12 max-w-xl mx-auto">
          Not sure which package is right for you? Tell us about your book idea and we'll recommend the best option.
        </p>
        <div className="max-w-lg mx-auto bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
          {contactStatus === 'sent' ? (
            <div className="text-center py-8" data-testid="contact-success">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Message Sent!</h3>
              <p className="text-gray-400">We'll get back to you within 24 hours. Check your email for a confirmation.</p>
            </div>
          ) : (
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
                <input
                  type="text" required
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Your name"
                  value={contactForm.name}
                  onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))}
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                <input
                  type="email" required
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="your@email.com"
                  value={contactForm.email}
                  onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))}
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Genre Interest</label>
                <select
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={contactForm.genre_interest}
                  onChange={e => setContactForm(f => ({ ...f, genre_interest: e.target.value }))}
                  data-testid="contact-genre"
                >
                  <option value="">Select a genre (optional)</option>
                  <option value="romance">Romance</option>
                  <option value="mystery">Mystery / Thriller</option>
                  <option value="scifi">Science Fiction</option>
                  <option value="fantasy">Fantasy</option>
                  <option value="selfhelp">Self-Help</option>
                  <option value="business">Business</option>
                  <option value="biography">Biography / Memoir</option>
                  <option value="howto">How-To / Guide</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Tell us about your book idea</label>
                <textarea
                  required rows={4}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  placeholder="Describe your book idea, target audience, and any questions..."
                  value={contactForm.message}
                  onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))}
                  data-testid="contact-message"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                disabled={contactStatus === 'sending'}
                data-testid="contact-submit"
              >
                {contactStatus === 'sending' ? 'Sending...' : (
                  <><Send className="w-4 h-4 mr-2" /> Send Message</>
                )}
              </Button>
              {contactStatus === 'error' && (
                <p className="text-red-400 text-sm text-center">Something went wrong. Please try again.</p>
              )}
            </form>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="container mx-auto px-4 py-16">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Write Your Book?
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
            Join hundreds of authors who've published their dreams with AI Ghostwriter Pro.
            Your book is waiting to be written.
          </p>
          <Button 
            size="lg"
            onClick={() => handleGetStarted('professional')}
            className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-8 py-6"
          >
            Start Your Book Today <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-purple-200 text-sm mt-4">
            No commitment required • See your outline before you pay
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-8 border-t border-slate-800">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <PenTool className="w-6 h-6 text-purple-400" />
            <span className="text-white font-semibold">AI Ghostwriter Pro</span>
            <span className="text-gray-500">by UPost</span>
          </div>
          <div className="flex items-center gap-6 text-gray-400 text-sm">
            <Link to="/terms" className="hover:text-white transition">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition">Privacy</Link>
            <Link to="/about" className="hover:text-white transition">About</Link>
            <Link to="/" className="hover:text-white transition">UPost.com</Link>
          </div>
          <div className="text-gray-500 text-sm">
            © 2026 Web-Tech Services. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default AIGhostwriterLanding;
